import { createSelector } from "@reduxjs/toolkit";
import type { PostMultiplacerDevicesSimilarRoomsWithDevicesApiArg } from "../../../../openapi";
import { openapi } from "../../../../openapi";
import { IRootState } from "../../../app/store";
import { selectActivePartialMcbimSpace } from "../../../McBim/mcbimSelectors";
import { selectHttpSingleRoomFull } from "../../../Room/httpRoomSelectors";
import { selectProjectNo } from "../../../globalSelectors";
import { getPercentColor } from "../../../RoomColoring";
import {
  selectCeilingSelectedSimilarRoomWithDevicesNumber,
  selectCeilingShowOnlyLockedRooms,
} from "../ceilingSlice";
import type { SimilarRoomWithDevices } from "./similarRoomsWithDevicesTypes";

const EMPTY_SIMILAR_ROOMS_WITH_DEVICES: SimilarRoomWithDevices[] = [];

export type SimilarRoomWithDevicesCardViewModel = {
  roomNumber: string;
  roomName: string;
  floorLabel: string;
  similarityColor: string;
  similarityPercent: number;
  locked: boolean;
  lockedByLabel: string | null;
  lockedAtLabel: string | null;
  isSelected: boolean;
};

const getSimilarityPercent = (score: number): number => Math.round(score * 100);

const formatLockedAt = (value: string | Date | undefined): string | null => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleDateString("nb-NO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export const selectSimilarRoomsWithDevicesCurrentRoomNumber = createSelector(
  [selectHttpSingleRoomFull, selectActivePartialMcbimSpace],
  (room, activeSpace): string | undefined => room?.roomNumber ?? activeSpace?.name
);

export const selectSimilarRoomsWithDevicesCurrentRoomName = createSelector(
  [selectHttpSingleRoomFull, selectActivePartialMcbimSpace],
  (room, activeSpace): string | undefined => room?.roomName ?? activeSpace?.longName
);

export const selectSimilarRoomsWithDevicesQueryArgs = createSelector(
  [
    selectProjectNo,
    selectSimilarRoomsWithDevicesCurrentRoomNumber,
    selectSimilarRoomsWithDevicesCurrentRoomName,
    selectCeilingShowOnlyLockedRooms,
  ],
  (
    projectNo,
    roomNumber,
    roomName,
    showOnlyLockedRooms
  ): PostMultiplacerDevicesSimilarRoomsWithDevicesApiArg | null => {
    if (!projectNo || !roomNumber || !roomName) {
      return null;
    }

    return {
      body: {
        projectNo,
        ...(showOnlyLockedRooms ? { locked: true } : {}),
        roomName,
        roomNumber,
      },
    };
  }
);

export const selectCachedSimilarRoomsWithDevices = createSelector(
  [selectSimilarRoomsWithDevicesQueryArgs, (state: IRootState) => state],
  (args, state): SimilarRoomWithDevices[] => {
    if (!args) {
      return EMPTY_SIMILAR_ROOMS_WITH_DEVICES;
    }

    return (
      openapi.endpoints.postMultiplacerDevicesSimilarRoomsWithDevices.select(args)(state).data ??
      EMPTY_SIMILAR_ROOMS_WITH_DEVICES
    );
  }
);

export const selectSimilarRoomsWithDevicesSortedBySimilarity = createSelector(
  [selectCachedSimilarRoomsWithDevices],
  (rooms): SimilarRoomWithDevices[] => [...rooms].sort((a, b) => b.similarityScore - a.similarityScore)
);

export const selectSimilarRoomWithDevicesCards = createSelector(
  [
    selectSimilarRoomsWithDevicesSortedBySimilarity,
    selectSimilarRoomsWithDevicesCurrentRoomName,
    selectCeilingSelectedSimilarRoomWithDevicesNumber,
  ],
  (rooms, roomName, selectedRoomNumber): SimilarRoomWithDevicesCardViewModel[] =>
    rooms.map((room) => {
      const similarityPercent = getSimilarityPercent(room.similarityScore);

      return {
        roomNumber: room.roomNumber,
        roomName: roomName ?? "-",
        floorLabel: room.floor ?? "Ukjent etasje",
        similarityColor: getPercentColor(similarityPercent),
        similarityPercent,
        locked: room.locked === true,
        lockedByLabel: room.lockedBy?.name ?? null,
        lockedAtLabel: formatLockedAt(room.lockedAt),
        isSelected: room.roomNumber === (selectedRoomNumber ?? rooms[0]?.roomNumber),
      };
    })
);

export const selectSelectedSimilarRoomWithDevices = createSelector(
  [selectCachedSimilarRoomsWithDevices, selectCeilingSelectedSimilarRoomWithDevicesNumber],
  (rooms, selectedRoomNumber): SimilarRoomWithDevices | undefined =>
    rooms.find((room) => room.roomNumber === selectedRoomNumber) ?? rooms[0]
);
